﻿import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CancelEventRequest,
  CreateEventRequest,
  CreateTicketTypeRequest,
  Event,
  EventStatus,
  EventSummary,
  TicketType,
  UpdateEventRequest,
  UpdateTicketTypeRequest,
} from "@ticketnjoy/types";

import { apiClient } from "@/lib/api-client";

export const eventsQueryKey = (status?: EventStatus) =>
  ["events", status ?? "all"] as const;
export const eventQueryKey = (slug: string) => ["events", "detail", slug] as const;

export function useEventsQuery(status?: EventStatus) {
  return useQuery<EventSummary[]>({
    queryKey: eventsQueryKey(status),
    queryFn: () =>
      apiClient<EventSummary[]>(
        status ? `/events?status=${status}` : "/events",
      ),
  });
}

export function useEventQuery(slug: string) {
  return useQuery<Event>({
    queryKey: eventQueryKey(slug),
    queryFn: () => apiClient<Event>(`/events/${slug}`),
    enabled: Boolean(slug),
  });
}

function invalidateEventCaches(qc: ReturnType<typeof useQueryClient>, slug?: string) {
  qc.invalidateQueries({ queryKey: ["events"] });
  if (slug) {
    qc.invalidateQueries({ queryKey: eventQueryKey(slug) });
  }
}

export function useCreateEventMutation() {
  const qc = useQueryClient();
  return useMutation<Event, Error, CreateEventRequest>({
    mutationFn: (body) =>
      apiClient<Event>("/events", { method: "POST", body }),
    onSuccess: (evt) => {
      qc.setQueryData(eventQueryKey(evt.slug), evt);
      invalidateEventCaches(qc);
    },
  });
}

export function useUpdateEventMutation(slug: string) {
  const qc = useQueryClient();
  return useMutation<Event, Error, UpdateEventRequest>({
    mutationFn: (body) =>
      apiClient<Event>(`/events/${slug}`, { method: "PUT", body }),
    onSuccess: (evt) => {
      qc.setQueryData(eventQueryKey(evt.slug), evt);
      invalidateEventCaches(qc, evt.slug);
    },
  });
}

type TransitionAction = "publish" | "pause" | "resume" | "archive";

export function useEventStatusMutation(slug: string) {
  const qc = useQueryClient();
  return useMutation<Event, Error, TransitionAction>({
    mutationFn: (action) =>
      apiClient<Event>(`/events/${slug}/${action}`, { method: "POST" }),
    onSuccess: (evt) => {
      qc.setQueryData(eventQueryKey(evt.slug), evt);
      invalidateEventCaches(qc, evt.slug);
    },
  });
}

export function useCancelEventMutation(slug: string) {
  const qc = useQueryClient();
  return useMutation<Event, Error, CancelEventRequest>({
    mutationFn: (body) =>
      apiClient<Event>(`/events/${slug}/cancel`, { method: "POST", body }),
    onSuccess: (evt) => {
      qc.setQueryData(eventQueryKey(evt.slug), evt);
      invalidateEventCaches(qc, evt.slug);
    },
  });
}

// ---- TicketType ----

export function useAddTicketTypeMutation(slug: string) {
  const qc = useQueryClient();
  return useMutation<TicketType, Error, CreateTicketTypeRequest>({
    mutationFn: (body) =>
      apiClient<TicketType>(`/events/${slug}/ticket-types`, {
        method: "POST",
        body,
      }),
    onSuccess: () => invalidateEventCaches(qc, slug),
  });
}

export function useUpdateTicketTypeMutation(slug: string, ticketTypeId: string) {
  const qc = useQueryClient();
  return useMutation<TicketType, Error, UpdateTicketTypeRequest>({
    mutationFn: (body) =>
      apiClient<TicketType>(
        `/events/${slug}/ticket-types/${ticketTypeId}`,
        { method: "PUT", body },
      ),
    onSuccess: () => invalidateEventCaches(qc, slug),
  });
}

export function useDeleteTicketTypeMutation(slug: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (ticketTypeId) =>
      apiClient<void>(`/events/${slug}/ticket-types/${ticketTypeId}`, {
        method: "DELETE",
      }),
    onSuccess: () => invalidateEventCaches(qc, slug),
  });
}
